import { IPromise, IHttpService, IRequestShortcutConfig } from "angular";

import {
  IPlanningSessionParams,
  IPlanningSession,
  IPlanningSessionConflict,
  IPlanningSessionStats,
  ISessionWorkflowStats,
  IPlanningSessionStatsAssignee,
} from "../models/models";
import { ICollection } from "../../models";
import { IGoal } from "../../goals/models";
import { IHubWorkflowActions, IHubWorkflowItemsCollection } from "../../hub/models/hub";
import { EnvironmentService } from "@gtmhub/env";
import { IMetric } from "@gtmhub/okrs/metrics";
import { GoalService } from "@gtmhub/goals/goalService";
import { IRestLayerRequest } from "@gtmhub/core";
import { ViewHistoryService } from "@gtmhub/view-history";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";

export class PlanningSessionService {
  public static $inject = ["$http", "EnvironmentService", "GoalService", "ViewHistoryService"];

  constructor(private $http: IHttpService, private env: EnvironmentService, private goalService: GoalService, private viewHistoryService: ViewHistoryService) {}

  public getGoalsHit(id: string, tag: string): IPromise<IGoal[]> {
    const url = this.env.getApiEndpoint("/sessions/" + id + "/stats/goalsMet");
    const query: IRequestShortcutConfig = {
      params: {
        tags: tag,
      },
    };

    return this.$http.get<ICollection<IGoal>>(url, query).then((response) => this.goalService.applyColoringToGoalCollection(response.data).items);
  }

  public getBadMetrics(id: string, tag: string): IPromise<IMetric[]> {
    const url = this.env.getApiEndpoint("/sessions/" + id + "/stats/badMetrics");
    const query: IRequestShortcutConfig = {
      params: {
        tags: tag,
      },
    };

    return this.$http.get<ICollection<IMetric>>(url, query).then((response) => this.goalService.applyColoringToMetricCollection(response.data).items);
  }

  public getGoodMetrics(id: string, tag: string): IPromise<IMetric[]> {
    const url = this.env.getApiEndpoint("/sessions/" + id + "/stats/goodMetrics");
    const query: IRequestShortcutConfig = {
      params: {
        tags: tag,
      },
    };

    return this.$http.get<ICollection<IMetric>>(url, query).then((response) => this.goalService.applyColoringToMetricCollection(response.data).items);
  }

  public getMetricsHit(id: string, tag: string): IPromise<IMetric[]> {
    const url = this.env.getApiEndpoint("/sessions/" + id + "/stats/metricsMet");
    const query: IRequestShortcutConfig = {
      params: {
        tags: tag,
      },
    };

    return this.$http.get<ICollection<IMetric>>(url, query).then((response) => this.goalService.applyColoringToMetricCollection(response.data).items);
  }

  public getManualMetrics(id: string, tag: string): IPromise<IMetric[]> {
    const url = this.env.getApiEndpoint("/sessions/" + id + "/stats/manualMetrics");
    const query: IRequestShortcutConfig = {
      params: {
        tags: tag,
      },
    };

    return this.$http.get<ICollection<IMetric>>(url, query).then((response) => this.goalService.applyColoringToMetricCollection(response.data).items);
  }

  public getDynamicMetrics(id: string, tag: string): IPromise<IMetric[]> {
    const url = this.env.getApiEndpoint("/sessions/" + id + "/stats/dynamicMetrics");
    const query: IRequestShortcutConfig = {
      params: {
        tags: tag,
      },
    };

    return this.$http.get<ICollection<IMetric>>(url, query).then((response) => this.goalService.applyColoringToMetricCollection(response.data).items);
  }

  /**
   * Returns the sessions in which a user or a team and its members have okrs or/and krs
   */
  public getRelevantSessions(target: { type: "user" | "team"; id: string }, params = {} as IPlanningSessionParams): IPromise<IPlanningSession[]> {
    const query: IRequestShortcutConfig = {
      params,
    };

    const url = this.env.getApiEndpointV2(`/sessions/${target.type}/${target.id}/relevant`);

    return this.$http.get<ICollection<IPlanningSession>>(url, query).then((response) => {
      return response.data.items;
    });
  }

  public getPlanningSessions(params?: IPlanningSessionParams): IPromise<IPlanningSession[]> {
    if (!params) {
      params = {};
    }

    if (!params.fields) {
      params.fields =
        "isSelfApproveAllowed,accountId,end,start,title,status,access,cadence,cadenceType,parentId,color,workflow,tags,demo,cadenceTimeZone,cadenceStart,settings";
    }

    const query: IRequestShortcutConfig = {
      params,
    };

    const url = this.env.getApiEndpoint("/sessions");

    return this.$http.get<ICollection<IPlanningSession>>(url, query).then((response) => {
      // some of callbacks invoked from the promise are directly mutating the result with data which isn't relevant for the sessions
      // this way it directly mutates the store which breaks all patterns of immutability and causes errors on other screen
      return angular.copy(response.data.items);
    });
  }

  public getSessionsV2(params: IRestLayerRequest): IPromise<ICollection<Partial<IPlanningSession>>> {
    if (!params.fields) {
      params.fields = "title";
    }

    const url = this.env.getApiEndpointV2("/sessions");

    const config: IRequestShortcutConfig = {
      params,
    };

    return this.$http.get<ICollection<Partial<IPlanningSession>>>(url, config).then((response) => response.data);
  }

  public createPlanningSession(session: IPlanningSession): IPromise<IPlanningSession> {
    const url = this.env.getApiEndpoint("/sessions");

    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          teamsWithSubteamsPermissionsCount: this.getTeamsWithSubteamsPermissionsCount(session),
        },
      },
    };

    return this.$http.post<IPlanningSession>(url, session, gtmhubAdditionalParams).then((response) => response.data);
  }

  public checkForConflictsInSession(planningSession: IPlanningSession, action: string): IPromise<ICollection<IPlanningSessionConflict>> {
    const url = this.env.getApiEndpoint("/sessions/" + planningSession.id + "/conflicts");

    let inherits = false;
    for (let i = 0; i < planningSession.access.permissions.length; i++) {
      if (planningSession.access.permissions[i].principalKind === "account") {
        inherits = true;
      }
    }

    planningSession.access.inherits = inherits;

    const requestPayload: { session: IPlanningSession; action: string } = {
      session: planningSession,
      action: action,
    };

    return this.$http.post<ICollection<IPlanningSessionConflict>>(url, requestPayload).then((response) => response.data);
  }

  public updatePlanningSession(planningSession: IPlanningSession): IPromise<IPlanningSession> {
    const url = this.env.getApiEndpoint("/sessions/" + planningSession.id);

    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          teamsWithSubteamsPermissionsCount: this.getTeamsWithSubteamsPermissionsCount(planningSession),
        },
      },
    };

    return this.$http.put<IPlanningSession>(url, planningSession, gtmhubAdditionalParams).then((response) => {
      this.viewHistoryService.update(navItemsFactory.buildSessionNavItem(planningSession));
      return response.data;
    });
  }

  public deletePlanningSession(id: string): IPromise<boolean> {
    const url = this.env.getApiEndpoint("/sessions/" + id);

    return this.$http.delete<boolean>(url).then((response) => response.data);
  }

  public stats(id: string, args?: { tags?: string; assigneeId?: string }): IPromise<IPlanningSessionStats> {
    const url = this.env.getApiEndpoint(`/sessions/${id}/stats`);

    const query: IRequestShortcutConfig = {
      params: args,
    };

    return this.$http.get<IPlanningSessionStats>(url, query).then((response) => response.data);
  }

  public statsAssignees(id: string, args?: { assigneeIds: string }): IPromise<IPlanningSessionStatsAssignee[]> {
    const url = this.env.getApiEndpoint(`/sessions/${id}/stats/assignees`);

    const query: IRequestShortcutConfig = {
      params: args,
    };

    return this.$http.get<IPlanningSessionStatsAssignee[]>(url, query).then((response) => response.data);
  }

  public getWorkflowStats(sessionId: string): IPromise<ISessionWorkflowStats> {
    const url: string = this.env.getApiEndpoint(`/sessions/${sessionId}/stats/workflow-stats`);

    return this.$http.get<ISessionWorkflowStats>(url).then((response) => response.data);
  }

  public nudgePeople(sessionId: string, audience: string, message: string): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/sessions/${sessionId}/nudge`);
    const payload: { audience: string; message: string } = {
      audience: audience,
      message: message,
    };

    return this.$http.post<unknown>(url, payload).then((response) => response.data);
  }

  public getCurrentUserActionsCount(): IPromise<IHubWorkflowActions> {
    const url: string = this.env.getApiEndpoint("/sessions/hub/workflow");

    return this.$http.get<IHubWorkflowActions>(url).then((response) => response.data);
  }

  public getCurrentUserWorkflowItems(fields?: string, workflowStatus?: string): IPromise<IHubWorkflowItemsCollection> {
    const url: string = this.env.getApiEndpoint("/sessions/hub/workflow-items");

    const query: IRequestShortcutConfig = {
      params: {},
    };

    if (fields && fields.trim().length > 0) {
      query.params.fields = fields;
    }

    if (workflowStatus) {
      query.params.workflowStatus = workflowStatus;
    }

    return this.$http.get<IHubWorkflowItemsCollection>(url, query).then((response) => response.data);
  }

  private getTeamsWithSubteamsPermissionsCount(session: IPlanningSession): number {
    if (!session.access || !session.access.permissions) {
      return 0;
    }
    let count = 0;
    session.access.permissions.forEach((permission) => {
      if (permission.principalKind === "team_and_subteams") {
        count++;
      }
    });

    return count;
  }
}
